import { useNavigate, Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import "../../styles/main.css"
import { useLanguage } from "../../utils/LanguageContext";
import api from "../../services/api";
import { CountUp, prefersReducedMotion } from "../../utils/motion";
import { getLocalized } from "../../utils/languageUtils";

const HERO_IMAGE = "/images/home-hero.webp";
const ABOUT_IMAGE = "/images/about-seedlings.webp";
const STICKER = (name) => `/images/stickers/${name}.webp`;
// Set this when a real promo video exists; the "Watch video" button stays hidden until then.
const PROMO_VIDEO_URL = "";


export default function Main() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ trees: 0, members: 0 });
  const [speciesCount, setSpeciesCount] = useState(0);
  const [products, setProducts] = useState([]);
  const [showVideo, setShowVideo] = useState(false);
  const visualRef = useRef(null);

  useEffect(() => {
    api.get('/stats/general/')
      .then(res => {
        const d = res.data || {};
        setStats({
          trees: d.countPLantedTrees || 0,
          members: d.countUsers || 0,
        });
      })
      .catch(err => console.error('Stats fetch error:', err));

    // Live number of tree species on sale (replaces the old hard-coded "12 regions").
    api.get('products/')
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        setProducts(list);
        setSpeciesCount(new Set(list.map(p => p.tree?.id ?? p.id)).size);
      })
      .catch(() => {});
  }, []);

  // Gentle parallax on the hero composition, driven by CSS variables.
  const handleHeroMove = (e) => {
    const el = visualRef.current;
    if (!el || prefersReducedMotion()) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    el.style.setProperty('--px', x.toFixed(3));
    el.style.setProperty('--py', y.toFixed(3));
  };

  const resetHeroMove = () => {
    const el = visualRef.current;
    if (!el) return;
    el.style.setProperty('--px', 0);
    el.style.setProperty('--py', 0);
  };

  const prices = products.map(p => Number(p.price)).filter(n => n > 0);
  const minPrice = prices.length ? Math.min(...prices) : 0;
  const featured = products.slice(0, 4);
  const fill = (text) => text
    .replace('{n}', speciesCount || '…')
    .replace('{price}', minPrice ? minPrice.toLocaleString() : '…');

  const howSteps = [
    { sticker: 'cart-sapling', title: 'home_how_1_t', desc: 'home_how_1_d' },
    { sticker: 'planting-tools', title: 'home_how_2_t', desc: 'home_how_2_d' },
    { sticker: 'phone-map', title: 'home_how_3_t', desc: 'home_how_3_d' },
  ];

  const whyCards = [
    { sticker: 'tree-verified', title: 'main_card_real_title', desc: 'main_card_real_desc' },
    { sticker: 'map', title: 'main_card_trans_title', desc: 'main_card_trans_desc' },
    { sticker: 'mascot', title: 'main_card_comm_title', desc: 'main_card_comm_desc' },
    { sticker: 'growth-chart', title: 'main_card_res_title', desc: 'main_card_res_desc' },
  ];


  return (
    <div className="home-page">
      {/* ================= HERO ================= */}
      <section id="home" className="home-hero" onMouseMove={handleHeroMove} onMouseLeave={resetHeroMove}>
        <div className="home-hero-bg" aria-hidden="true">
          <span className="blob blob-1" />
          <span className="blob blob-2" />
          <span className="blob blob-3" />
          <svg className="topo-lines" viewBox="0 0 600 600" preserveAspectRatio="none">
            <path d="M0 420 C120 380 200 460 320 420 S520 340 600 380" />
            <path d="M0 460 C140 420 220 500 340 460 S520 380 600 420" />
            <path d="M0 500 C160 460 240 540 360 500 S540 420 600 460" />
            <path d="M0 140 C100 100 220 180 330 130 S520 60 600 110" />
            <path d="M0 180 C120 140 240 220 350 170 S540 100 600 150" />
          </svg>
        </div>

        <div className="home-hero-container">
          <div className="home-hero-content">
            <div className="home-hero-badge hero-enter" style={{ '--d': '0.05s' }}>
              <span className="pulse-dot" />
              <span>{t('main_hero_badge')}</span>
            </div>

            <h1 className="home-hero-title">
              <span className="line"><span className="hero-enter" style={{ '--d': '0.15s' }}>{t('main_hero_title_1')}</span></span>
              <span className="line">
                <span className="highlight serif-accent hero-enter" style={{ '--d': '0.3s' }}>
                  {t('main_hero_title_2')}
                  <svg className="scribble" viewBox="0 0 300 20" preserveAspectRatio="none" aria-hidden="true">
                    <path d="M3 14 C 60 4, 120 4, 170 10 S 260 18, 297 6" />
                  </svg>
                </span>
              </span>
            </h1>

            <p className="home-hero-desc hero-enter" style={{ '--d': '0.45s' }}>
              {t('main_hero_desc')}
            </p>

            <div className="home-hero-buttons hero-enter" style={{ '--d': '0.6s' }}>
              <button className="btn-primary" onClick={() => navigate('/shop')}>
                <i className="fas fa-tree" />
                <span>{t('main_btn_start')}</span>
                <i className="fas fa-arrow-right btn-arrow" />
              </button>
              {PROMO_VIDEO_URL && (
                <button className="btn-secondary" onClick={() => setShowVideo(true)}>
                  <span className="play-ring"><i className="fas fa-play" /></span>
                  <span>{t('main_btn_watch')}</span>
                </button>
              )}
            </div>

            <div className="home-hero-stats hero-enter" style={{ '--d': '0.75s' }}>
              <div className="hero-stat-item">
                <CountUp value={stats.trees} suffix="+" className="hero-stat-number" />
                <span className="hero-stat-label">{t('main_stat_trees')}</span>
              </div>
              <div className="hero-stat-item">
                <CountUp value={stats.members} suffix="+" className="hero-stat-number" />
                <span className="hero-stat-label">{t('main_stat_members')}</span>
              </div>
              <div className="hero-stat-item">
                <CountUp value={speciesCount} className="hero-stat-number" />
                <span className="hero-stat-label">{t('main_stat_species')}</span>
              </div>
            </div>
          </div>

          <div className="home-hero-visual" ref={visualRef}>
            <div className="visual-ring" aria-hidden="true" />
            <div className="visual-frame hero-enter-scale">
              <img src={HERO_IMAGE} alt="" />
              <div className="visual-frame-shade" />
            </div>

            <div className="hero-floating-card card-1">
              <img className="floating-card-sticker" src={STICKER('leaf')} alt="" />
              <div className="floating-card-text">
                <span className="floating-card-label">{t('main_float_co2_label')}</span>
                <span className="floating-card-value">{t('main_float_co2_val')}</span>
              </div>
            </div>

            <div className="hero-floating-card card-2">
              <img className="floating-card-sticker" src={STICKER('sapling-pin')} alt="" />
              <div className="floating-card-text">
                <span className="floating-card-label">{t('main_float_comm_label')}</span>
                <span className="floating-card-value">{t('main_float_comm_val')}</span>
              </div>
            </div>

            <img className="floating-leaf leaf-a" src={STICKER('leaf')} alt="" aria-hidden="true" />
            <img className="floating-leaf leaf-b" src={STICKER('cat-flowers')} alt="" aria-hidden="true" />
          </div>
        </div>

        <a href="#how" className="scroll-cue" aria-label="Scroll">
          <span />
        </a>
      </section>

      {/* ================= HOW IT WORKS (product intro) ================= */}
      <section id="how" className="home-section how-section">
        <div className="home-container">
          <div className="section-head centered">
            <div className="section-label">{t('home_how_label')}</div>
            <h2 className="section-title">{t('home_how_title')}</h2>
          </div>
          <div className="how-grid">
            {howSteps.map((step, i) => (
              <div className="how-card" key={step.title} data-reveal="">
                <span className="how-num">{i + 1}</span>
                <img className="how-sticker" src={STICKER(step.sticker)} alt="" loading="lazy" />
                <h3>{t(step.title)}</h3>
                <p>{fill(t(step.desc))}</p>
                {i < howSteps.length - 1 && <i className="fas fa-arrow-right how-arrow" aria-hidden="true" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FEATURED TREES (live from the shop) ================= */}
      {featured.length > 0 && (
        <section className="home-section featured-section">
          <div className="home-container">
            <div className="featured-head">
              <div>
                <div className="section-label">{t('home_feat_label')}</div>
                <h2 className="section-title">{t('home_feat_title')}</h2>
              </div>
              <Link to="/shop" className="featured-all">
                {t('home_feat_all')} <i className="fas fa-arrow-right" />
              </Link>
            </div>
            <div className="featured-grid">
              {featured.map(product => (
                <Link to={`/product/${product.id}`} className="featured-card" key={product.id} data-reveal="">
                  <div className="featured-img">
                    <img src={product.tree?.image} alt={getLocalized(product.tree, language, 'name') || ''} loading="lazy" />
                  </div>
                  <div className="featured-body">
                    <h3>{getLocalized(product.tree, language, 'name')}</h3>
                    <div className="featured-price">
                      {Number(product.price).toLocaleString()} <span>UZS</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ================= ABOUT ================= */}
      <section id="about" className="home-section about-section">
        <div className="home-container about-grid">
          <div className="about-collage" data-reveal="left">
            <div className="collage-main">
              <img src={ABOUT_IMAGE} alt="" loading="lazy" />
            </div>
            <div className="collage-badge">
              <span className="collage-badge-num"><CountUp value={stats.trees} suffix="+" /></span>
              <span className="collage-badge-label">{t('main_stat_trees')}</span>
            </div>
          </div>

          <div className="about-content">
            <div className="section-label">{t('main_about_label')}</div>
            <h2 className="section-title">{t('main_about_title')}</h2>
            <p className="section-subtitle">{t('main_about_subtitle')}</p>
            <div className="feature-list">
              {[1, 2, 3, 4, 5, 6].map(n => (
                <div className="feature-item" key={n}>
                  <span className="feature-icon"><i className="fas fa-check" /></span>
                  <p className="feature-text">{t(`main_check_${n}`)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= WHY US ================= */}
      <section id="features" className="home-section why-section">
        <div className="home-container">
          <div className="section-head centered">
            <div className="section-label">{t('main_why_label')}</div>
            <h2 className="section-title">{t('main_why_title')}</h2>
            <p className="section-subtitle">{t('main_why_subtitle')}</p>
          </div>
          <div className="why-grid">
            {whyCards.map((c, i) => (
              <div className="process-rect why-card" key={c.title}>
                <span className="why-index">0{i + 1}</span>
                <div className="process-rect-icon">
                  <img src={STICKER(c.sticker)} alt="" loading="lazy" />
                </div>
                <h3>{t(c.title)}</h3>
                <p>{t(c.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= IMPACT ================= */}
      <section className="home-section impact-section">
        <div className="impact-rings" aria-hidden="true">
          <span /><span /><span />
        </div>
        <div className="home-container">
          <div className="section-head centered">
            <h2 className="section-title">{t('main_impact_title')}</h2>
            <p className="section-subtitle">{t('main_impact_subtitle')}</p>
          </div>
          <div className="impact-grid">
            <div className="stat-item">
              <span className="stat-icon"><img src={STICKER('sapling-pin')} alt="" loading="lazy" /></span>
              <CountUp value={stats.trees} suffix="+" className="stat-number" />
              <div className="stat-label">{t('main_stat_trees')}</div>
            </div>
            <div className="stat-item">
              <span className="stat-icon"><img src={STICKER('mascot')} alt="" loading="lazy" /></span>
              <CountUp value={stats.members} suffix="+" className="stat-number" />
              <div className="stat-label">{t('main_stat_members')}</div>
            </div>
            <div className="stat-item">
              <span className="stat-icon"><img src={STICKER('cat-fruit')} alt="" loading="lazy" /></span>
              <CountUp value={speciesCount} className="stat-number" />
              <div className="stat-label">{t('main_stat_species')}</div>
            </div>
          </div>
        </div>
      </section>

      {showVideo && PROMO_VIDEO_URL && (
        <div className="video-modal" role="dialog" aria-modal="true" onClick={() => setShowVideo(false)}>
          <div className="video-modal-inner" onClick={(e) => e.stopPropagation()}>
            <button className="video-modal-close" aria-label="Close" onClick={() => setShowVideo(false)}>
              <i className="fas fa-times" />
            </button>
            <iframe src={PROMO_VIDEO_URL} title="EcoNovda" allow="autoplay; fullscreen" allowFullScreen />
          </div>
        </div>
      )}
    </div>
  );
}
